import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  CourseDetails,
  GetCourseDetailsResponse,
} from "redux/api/types";

import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Grid,
  MenuItem,
  Select,
  Checkbox,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import FolderIcon from "@mui/icons-material/Folder";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import YouTubeIcon from "@mui/icons-material/YouTube";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import weeksToDays from "date-fns/esm/weeksToDays";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ContentLanguageSelect from "./ContentLanguageSelect";
import { getContentLanguage } from "../utils/contentLanguage";

// Feature flag: set to true to re-enable the "Assessments" and
// "Learning Experience (AI Instructor & Extras)" sections in the dialog.
const SHOW_ASSESSMENTS_AND_LEARNING_EXPERIENCE = false;

type CurriculumWeek = {
  week: number;
  title: string;
  subtopics?: string[];
  exercise?: string;
  assignment?: string;
};

// Shape of the `selectedCourse` object passed in from the parent.
// Fields are optional since the dialog should still render sensibly
// with no data (e.g. a "create new course" flow).
export type CourseData = {
  type?: string;
  title?: string;
  name?: string;
  hook?: string;
  description?: string;
  image_url?: string;
  target_audience?: string;
  duration?: string;
  pricing?: string;
  learning_outcomes?: string[];
  key_points?: string[];
  curriculum?: CurriculumWeek[];
  [key: string]: any;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  onGenerate?: (data: any) => void;
  onOptimize?: () => void;
  data?: CourseData;
  // Course whose uploaded content (documents/audio/video) should populate
  // the Knowledge Library. Falls back to localStorage("course_id") — same
  // source used on the My Digital Twin screen — if not provided.
  courseId?: string;
};

/* ---------------------------------------------------------- */
/* STATIC OPTION DATA - swap these for API-driven data later   */
/* ---------------------------------------------------------- */

const courseTypeOptions = [
  { label: "Professional Course", sub: "10–20 Hours" },
  { label: "Academic Course", sub: "15–25 Hours" },
  { label: "Certification Course", sub: "20–30 Hours" },
];

//const durationOptions = ["2 Weeks", "4 Weeks",  "8 Weeks", "12 Weeks", "16 Weeks"];
const teachingStyleOptions = [
  "Corporate Training",
  "Academic",
  "Storytelling",
  "Hands-on Workshop",
];
//const learnerLevelOptions = ["Beginner", "Intermediate", "Advanced"];
const learnerLevelOptions = [
  "Early Elementary — Grades 1–2",
  "Elementary — Grades 3–5",
  "Middle School — Grades 6–8",
  "High School — Grades 9–12",
  "College / University",
  "Professional / Adult",
  "Expert / Technical",
];

//const chapterOptions = ["6 Chapters", "8 Chapters", "10 Chapters"];
const weekOptions = Array.from({ length: 16 }, (_, i) => {
  const n = i + 1;
  return `${n} Module${n === 1 ? "" : "s"}`;
});
const lessonsPerChapterOptions = ["1 Lessons", "2 Lessons", "3 Lessons", "4 Lessons", "5 Lessons", "6 Lessons"];
const lessonLengthOptions = [
  "Short (5–10 min)",
  "Medium (10–20 min)",
  "Long (20–30 min)",
];

const lessonQuizOptions = ["2 Questions", "4 Questions", "6 Questions"];
const chapterQuizOptions = ["5 Questions", "10 Questions", "15 Questions"];
const finalExamOptions = ["25 Questions", "50 Questions", "75 Questions"];

// Ensures a data-derived value (e.g. "4 weeks" coming from selectedCourse)
// always shows correctly in a Select even if it isn't one of the presets.
const mergeOption = (base: string[], value?: string) =>
  value && !base.includes(value) ? [value, ...base] : base;

// One row in the Knowledge Library list, sourced from the same uploaded
// content shown on the My Digital Twin screen (documents, text notes,
// audio, video, website links, YouTube links) via
// POST /api/prompts/get_course_details.
//
// NOTE: the live API does not reliably match `redux/api/types`. Documents/
// audio/video are typed as { name, size|duration } with no id at all, while
// text_documents is typed as string[] but actually returns
// { value, doc_id }, and web_urls/youtube_urls are typed as string[] but
// return { url, status, progress }. `resolveRealId` below picks up a real
// id (doc_id/_id/id/document_id) wherever the API happens to provide one
// (currently only text notes) and falls back to a synthetic, positional
// reference (`${type}-${index}`) everywhere else — matching how the rest
// of the app already addresses these items by content-type + index.
type KnowledgeLibraryItem = {
  id: string;
  name: string;
  type: string; // display tag, e.g. "PDF", "DOCX", "MP3", "TEXT"
  meta?: string; // size/duration/status, if available
};

// Shape actually sent to save-course: `type` is normalized to a broad
// category ("video", "audio", "document", ...) instead of a raw extension,
// with the original extension preserved in `format` (e.g. "MP4", "MP3").
type KnowledgeLibraryPayloadItem = Omit<KnowledgeLibraryItem, "type"> & {
  type: string;
  format: string;
};

// Resolves an icon + color for a file based on its type/extension string.
// Falls back to a generic file icon for anything unrecognized.
const getFileVisual = (type: string) => {
  const t = (type || "").toLowerCase();
  if (t.includes("pdf")) {
    return { icon: <PictureAsPdfIcon fontSize="small" />, color: "#F87171" };
  }
  if (t.includes("doc")) {
    return { icon: <DescriptionIcon fontSize="small" />, color: "#60A5FA" };
  }
  if (t.includes("ppt")) {
    return { icon: <DescriptionIcon fontSize="small" />, color: "#FB923C" };
  }
  if (["mp3", "wav", "m4a", "audio"].some((x) => t.includes(x))) {
    return { icon: <AudiotrackIcon fontSize="small" />, color: "#C084FC" };
  }
  if (["mp4", "mov", "avi", "video"].some((x) => t.includes(x))) {
    return { icon: <MovieOutlinedIcon fontSize="small" />, color: "#34D399" };
  }
  if (t.includes("txt") || t.includes("text")) {
    return { icon: <DescriptionOutlinedIcon fontSize="small" />, color: "#FBBF24" };
  }
  if (t.includes("youtube")) {
    return { icon: <YouTubeIcon fontSize="small" />, color: "#F87171" };
  }
  if (t.includes("web") || t.includes("url")) {
    return { icon: <LanguageIcon fontSize="small" />, color: "#38BDF8" };
  }
  return { icon: <FolderIcon fontSize="small" />, color: "#A78BFA" };
};

// Maps a display type (e.g. "MP4", "MP3", "PDF") to the broad category the
// save-course payload expects in its `type` field (e.g. "video", "audio").
// The original extension is preserved separately as `format` — see
// `handleGenerate` below, where this is applied only to the outgoing
// payload so the Knowledge Library UI can keep showing the specific
// extension (MP3/MP4/etc.) per row.
const getItemCategory = (type: string): string => {
  const t = (type || "").toLowerCase();
  if (["mp3", "wav", "m4a", "audio"].some((x) => t.includes(x))) return "audio";
  if (["mp4", "mov", "avi", "video"].some((x) => t.includes(x))) return "video";
  if (t.includes("pdf") || t.includes("doc") || t.includes("ppt")) return "document";
  if (t.includes("txt") || t.includes("text")) return "text";
  if (t.includes("youtube")) return "youtube";
  if (t.includes("web") || t.includes("url")) return "web";
  return "file";
};

const generationModes = [
  {
    key: "faithful",
    label: "Faithful to my content",
    desc: "Stay close to my original material",
  },
  {
    key: "balanced",
    label: "Balanced",
    desc: "Mix my content with AI enhancements",
  },
  {
    key: "expanded",
    label: "Expanded with AI",
    desc: "Add examples, insights, and depth",
  },
];

const learningExperienceItems = [
  { key: "digitalTwin", icon: <PersonOutlineIcon />, label: "Digital Twin Instructor" },
  { key: "videoIntros", icon: <VideocamOutlinedIcon />, label: "Video Introductions" },
  { key: "workbook", icon: <MenuBookIcon />, label: "Downloadable Workbook" },
  { key: "realWorld", icon: <LightbulbOutlinedIcon />, label: "Real-world Examples" },
  { key: "discussion", icon: <ForumOutlinedIcon />, label: "Discussion Questions" },
  { key: "certificate", icon: <WorkspacePremiumIcon />, label: "Certificate of Completion" },
] as const;


const defaultCourseOutlinePreview = [
  {
    week: "Module 1: Introduction to Venture Capital",
    lessons: [
      "What is Venture Capital?",
      "The VC Ecosystem",
      "Key Players & Roles",
      "How VC Creates Value",
      "Chapter Summary",
    ],
  },
  {
    week: "Module 2: Sourcing & Deal Flow",
    lessons: [
      "Finding Great Opportunities",
      "Evaluating Markets",
      "Deal Flow Strategies",
    ],
  },
];
const defaultWhatInclude = [
  "8 Modules",
  "40 Lessons",
  "Video Introductions",
  "168+ Lesson Quiz Questions",
  "50 Final Exam Questions",
  "Downloadable Workbook",
  "Completion Certificate",
  "Course Resources",
];

/* ---------------------------------------------------------- */
/* SHARED STYLES                                               */
/* ---------------------------------------------------------- */

const fieldSx = {
  background: "rgba(255,255,255,0.03)",
  borderRadius: "14px",
  color: "#fff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.08)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.15)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#8B5CF6",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 1.3,
  },
};

const cardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "rgba(255,255,255,0.02)",
  p: 2,
};

const panelSx = {
  borderRadius: "20px",
  border: "1px solid rgba(255,255,255,0.08)",
  background:
    "linear-gradient(180deg, rgba(17,24,39,0.96) 0%, rgba(8,12,20,0.96) 100%)",
  p: 2.5,
};

const sectionTitleSx = {
  color: "#fff",
  fontWeight: 700,
  fontSize: 16,
  mb: 0.3,
};

const sectionSubtitleSx = {
  color: "rgba(255,255,255,0.45)",
  fontSize: 12.5,
  mb: 1.8,
};

const fieldLabelSx = {
  color: "rgba(255,255,255,0.55)",
  fontSize: 12.5,
  mb: 0.8,
};

/* ---------------------------------------------------------- */
/* REUSABLE FIELD COMPONENTS                                   */
/* ---------------------------------------------------------- */

const InfoDot = () => (
  <InfoOutlinedIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.3)" }} />
);

const IconSelect = ({
  icon,
  value,
  onChange,
  options,
  sublabel,
}: {
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  sublabel?: string;
}) => (
  <Select
    value={value}
    onChange={(e) => onChange(e.target.value as string)}
    fullWidth
    IconComponent={KeyboardArrowDownIcon}
    sx={fieldSx}
    renderValue={(val) => (
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box sx={{ color: "#A78BFA", display: "flex", fontSize: 20 }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
            {val as string}
          </Typography>
          {sublabel && (
            <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11.5 }}>
              {sublabel}
            </Typography>
          )}
        </Box>
      </Stack>
    )}
  >
    {options.map((opt) => (
      <MenuItem key={opt} value={opt}>
        {opt}
      </MenuItem>
    ))}
  </Select>
);

const FieldBlock = ({
  label,
  info,
  children,
}: {
  label: string;
  info?: boolean;
  children: React.ReactNode;
}) => (
  <Box>
    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.8 }}>
      <Typography sx={{ ...fieldLabelSx, mb: 0 }}>{label}</Typography>
      {info && <InfoDot />}
    </Stack>
    {children}
  </Box>
);

const CheckCard = ({
  checked,
  onChange,
  title,
  subtitle,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  subtitle: string;
}) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Checkbox
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      sx={{
        p: 0.4,
        color: "rgba(255,255,255,0.25)",
        "&.Mui-checked": { color: "#8B5CF6" },
      }}
    />
    <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 13 }}>
      {subtitle ? title : title}
    </Typography>
  </Stack>
);

const RadioOption = ({
  selected,
  onClick,
  title,
  subtitle,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
}) => (
  <Stack
    direction="row"
    spacing={1.2}
    alignItems="flex-start"
    onClick={onClick}
    sx={{ cursor: "pointer" }}
  >
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        mt: 0.3,
        flexShrink: 0,
        border: selected
          ? "5px solid #8B5CF6"
          : "2px solid rgba(255,255,255,0.3)",
        background: "#0d1017",
        transition: "0.15s ease",
      }}
    />
    <Box>
      <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
        {subtitle}
      </Typography>
    </Box>
  </Stack>
);

const ActionCard = ({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) => (
  <Box
    sx={{
      ...cardSx,
      p: 1.6,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 1.2,
      "&:hover": { borderColor: "rgba(139,92,246,0.4)" },
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        background: "rgba(139,92,246,0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#A78BFA",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>
        {title}
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const IconTile = ({
  icon,
  label,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: () => void;
}) => (
  <Box sx={{ ...cardSx, p: 1.4, textAlign: "center" }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "12px",
        mx: "auto",
        mb: 1,
        background:
          "linear-gradient(135deg, rgba(124,58,237,0.22), rgba(147,51,234,0.12))",
        border: "1px solid rgba(139,92,246,0.25)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#A78BFA",
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        color: "#fff",
        fontSize: 11.5,
        fontWeight: 600,
        lineHeight: 1.3,
        minHeight: 30,
      }}
    >
      {label}
    </Typography>
    <Checkbox
      checked={checked}
      onChange={onChange}
      size="small"
      sx={{
        p: 0.3,
        mt: 0.3,
        color: "rgba(255,255,255,0.25)",
        "&.Mui-checked": { color: "#8B5CF6" },
      }}
    />
  </Box>
);

/* ---------------------------------------------------------- */
/* MAIN COMPONENT                                               */
/* ---------------------------------------------------------- */

const CustomizeCourseDialog: React.FC<Props> = ({
  open,
  onClose,
  onBack,
  onGenerate,
  onOptimize,
  data,
  courseId,
}) => {
  /* -------------------------------------------------------- */
  /* Course Content / Knowledge Library — real uploaded content */
  /* (same source as the My Digital Twin screen: documents,     */
  /* text notes, audio, video via get_course_details)           */
  /* -------------------------------------------------------- */
  const [libraryItems, setLibraryItems] = useState<KnowledgeLibraryItem[]>([]);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState<Set<string>>(
    new Set()
  );
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  // Pagination — the library can hold dozens (or more) items, so we page
  // the list instead of rendering everything into the scroll container.
  const LIBRARY_PAGE_SIZE = 8;
  const [libraryPage, setLibraryPage] = useState(1);


  // Free-tier gating: level_0a members are locked to the default course
  // structure (4 Modules / 2 Lessons per Module) and can't change it.
  const [subscriptionLevel] = useState(() =>
    localStorage.getItem("subscription_level")
  );

  const isFreeTier = subscriptionLevel === "level_0a";
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const showUpgradeMessage = () => setUpgradeDialogOpen(true);

  const handleWeeksChange = (v: string) => {
    if (isFreeTier) {
      showUpgradeMessage();
      return;
    }
    setWeeks(v);
  };

  const handleLessonsPerChapterChange = (v: string) => {
    if (isFreeTier) {
      showUpgradeMessage();
      return;
    }
    setLessonsPerChapter(v);
  };

  // The live API doesn't consistently match `redux/api/types`. Some entries
  // (e.g. text_documents) actually carry a real id (`doc_id`) even though
  // the type/other content types don't. Prefer any real id we can find and
  // only fall back to a positional reference when nothing is available.
  const makeId = (type: string, index: number) => `${type}-${index}`;
  const resolveRealId = (entry: any): string | undefined =>
    entry?.doc_id || entry?._id || entry?.id || entry?.document_id;

  const getExtension = (name?: string) => {
    if (!name || !name.includes(".")) return "";
    return name.split(".").pop()?.toUpperCase() || "";
  };

  // Truncate long text-note content for the list row.
  const truncate = (text: string, max = 60) =>
    text.length > max ? `${text.slice(0, max).trim()}…` : text;

  const fetchKnowledgeLibrary = async () => {
    const resolvedCourseId =
      courseId || data?.parent_course_id || localStorage.getItem("course_id");
    if (!resolvedCourseId) return;

    setLibraryLoading(true);
    setLibraryError(null);
    try {
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const { data: res } = await axios.post<GetCourseDetailsResponse>(
        `${apiUrl}/api/prompts/get_course_details`,
        { course_id: resolvedCourseId }
      );
      const course: CourseDetails | undefined = res?.result;

      const documents: KnowledgeLibraryItem[] = ((course as any)?.documents || []).map(
        (d: any, i: number) => ({
          id: resolveRealId(d) || makeId("doc", i),
          name: d?.name || `Document ${i + 1}`,
          type: getExtension(d?.name) || "FILE",
          meta: d?.size,
        })
      );

      // NOTE: `redux/api/types` declares text_documents as string[], but the
      // live API actually returns objects like { value, doc_id } — handle
      // both shapes defensively, and use the real doc_id when present.
      const textDocuments: KnowledgeLibraryItem[] = (
        (course as any)?.text_documents || []
      ).map((t: any, i: number) => {
        const rawText =
          typeof t === "string" ? t : t?.value ?? t?.text ?? "";
        return {
          id: resolveRealId(t) || makeId("text", i),
          name: rawText ? truncate(rawText) : `Text Note ${i + 1}`,
          type: "TEXT",
        };
      });

      const audioFiles: KnowledgeLibraryItem[] = ((course as any)?.audio || []).map(
        (a: any, i: number) => ({
          id: resolveRealId(a) || makeId("audio", i),
          name: a?.name || `Audio ${i + 1}`,
          type: getExtension(a?.name) || "AUDIO",
          meta: a?.duration,
        })
      );

      const videoFiles: KnowledgeLibraryItem[] = ((course as any)?.video || []).map(
        (v: any, i: number) => ({
          id: resolveRealId(v) || makeId("video", i),
          name: v?.name || `Video ${i + 1}`,
          type: getExtension(v?.name) || "VIDEO",
          meta: v?.duration,
        })
      );

      // NOTE: `redux/api/types` declares web_urls/youtube_urls as string[],
      // but the live API actually returns objects like
      // { url, status, progress } — handle both shapes defensively.
      const asUrlString = (entry: any): string =>
        typeof entry === "string" ? entry : entry?.url || "";
      const asUrlStatus = (entry: any): string | undefined =>
        typeof entry === "string" ? undefined : entry?.status;

      const webLinks: KnowledgeLibraryItem[] = (
        (course as any)?.web_urls || []
      ).map((w: any, i: number) => ({
        id: resolveRealId(w) || makeId("web", i),
        name: asUrlString(w) || `Website ${i + 1}`,
        type: "WEB",
        meta: asUrlStatus(w),
      }));

      const youtubeLinks: KnowledgeLibraryItem[] = (
        (course as any)?.youtube_urls || []
      ).map((y: any, i: number) => ({
        id: resolveRealId(y) || makeId("youtube", i),
        name: asUrlString(y) || `YouTube Video ${i + 1}`,
        type: "YOUTUBE",
        meta: asUrlStatus(y),
      }));

      const items = [
        ...documents,
        ...textDocuments,
        ...audioFiles,
        ...videoFiles,
        ...webLinks,
        ...youtubeLinks,
      ];
      setLibraryItems(items);
      // Default to everything selected, same as the previous static mock.
      setSelectedLibraryIds(new Set(items.map((i) => i.id)));
    } catch (err) {
      console.error("Failed to load knowledge library", err);
      setLibraryError("Couldn't load your uploaded content.");
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchKnowledgeLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courseId, data?.parent_course_id]);

  const filteredLibraryItems = librarySearch
    ? libraryItems.filter((i) =>
        i.name?.toLowerCase().includes(librarySearch.toLowerCase())
      )
    : libraryItems;

  const libraryPageCount = Math.max(
    1,
    Math.ceil(filteredLibraryItems.length / LIBRARY_PAGE_SIZE)
  );

  // Keep the current page in range whenever the underlying/filtered list
  // changes size (e.g. a search narrows the results, or content loads in).
  useEffect(() => {
    if (libraryPage > libraryPageCount) {
      setLibraryPage(libraryPageCount);
    }
  }, [libraryPageCount, libraryPage]);

  // Jump back to page 1 whenever the search term changes.
  useEffect(() => {
    setLibraryPage(1);
  }, [librarySearch]);

  const pagedLibraryItems = filteredLibraryItems.slice(
    (libraryPage - 1) * LIBRARY_PAGE_SIZE,
    libraryPage * LIBRARY_PAGE_SIZE
  );

  const selectedLibraryCount = selectedLibraryIds.size;

  // Whether every currently-filtered item (i.e. everything matching the
  // active search, across all pages) is selected — used to decide whether
  // the "Select/Deselect All" button selects or clears.
  const allFilteredLibrarySelected =
    filteredLibraryItems.length > 0 &&
    filteredLibraryItems.every((i) => selectedLibraryIds.has(i.id));

  const toggleLibraryItem = (id: string) =>
    setSelectedLibraryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  // Selects or deselects every item matching the current search (not just
  // the current page), so "Select All" behaves predictably alongside
  // pagination and search.
  const toggleSelectAllLibrary = () =>
    setSelectedLibraryIds((prev) => {
      if (allFilteredLibrarySelected) {
        const next = new Set(prev);
        filteredLibraryItems.forEach((i) => next.delete(i.id));
        return next;
      }
      const next = new Set(prev);
      filteredLibraryItems.forEach((i) => next.add(i.id));
      return next;
    });

  const knowledgeCoverage = 92;

  /* -------------------------------------------------------- */
  /* Derive sensible defaults from the incoming course data     */
  /* -------------------------------------------------------- */
  const curriculum = data?.curriculum ?? [];

  // const deriveChapters = (d?: CourseData) =>
  //   d?.curriculum?.length ? `${d.curriculum.length} Chapters` : "8 Chapters";
  // const deriveWeeks = (d?: CourseData) =>
  // d?.curriculum?.length ? `${d.curriculum.length} Modules` : "4 Modules";
  const deriveWeeks = (d?: CourseData) => "4 Modules";

  // const deriveLessonsPerChapter = (d?: CourseData) => {
  //   if (!d?.curriculum?.length) return "5 Lessons";
  //   const avg =
  //     d.curriculum.reduce((sum, w) => sum + (w.subtopics?.length || 0), 0) /
  //     d.curriculum.length;
  //   return `${Math.max(1, Math.round(avg))} Lessons`;
  // };

  const deriveLessonsPerChapter = (d?: CourseData) => "2 Lessons";

  // const deriveDuration = (d?: CourseData) => {
  //   const raw = d?.duration;
  //   if (raw && /week/i.test(raw)) {
  //     // Normalize casing/spacing so it matches the static list style,
  //     // e.g. "4 weeks" -> "4 Weeks"
  //     const match = raw.match(/(\d+)\s*week/i);
  //     if (match) return `${match[1]} Weeks`;
  //     return raw;
  //   }
  //   return "4 Weeks";
  // };

  const derivePrice = (d?: CourseData) =>
    d?.pricing && !Number.isNaN(Number(d.pricing))
      ? Number(d.pricing).toFixed(2)
      : "79.00";

  const derivePricingType = (d?: CourseData): "paid" | "free" =>
    d?.pricing && Number(d.pricing) > 0 ? "paid" : "free";

  // Course Structure
  //const [chapters, setChapters] = useState(() => deriveChapters(data));
  const [weeks, setWeeks] = useState(() => deriveWeeks(data));
  const [lessonsPerChapter, setLessonsPerChapter] = useState(() =>
    deriveLessonsPerChapter(data)
  );
  const [lessonLength, setLessonLength] = useState("Short (5–10 min)");

  // Feature flag: set to true to re-enable the "Lesson Length" field
  // in the Course Structure section.
  const SHOW_LESSON_LENGTH = false;

  // Course Settings
  const [courseType, setCourseType] = useState(courseTypeOptions[0].label);
  //const [duration, setDuration] = useState(() => deriveDuration(data));
  const [teachingStyle, setTeachingStyle] = useState("Corporate Training");
  //const [learnerLevel, setLearnerLevel] = useState("Intermediate");
  const [learnerLevel, setLearnerLevel] = useState("Professional / Adult");
  const [generationMode, setGenerationMode] = useState("balanced");
  // Language of this course (defaults to the current content language)
  const [contentLanguage, setContentLanguage] = useState<string>(getContentLanguage());
  useEffect(() => {
    if (open) setContentLanguage(getContentLanguage());
  }, [open]);

  // Assessments
  const [lessonQuizOn, setLessonQuizOn] = useState(true);
  const [lessonQuizCount, setLessonQuizCount] = useState("4 Questions");
  const [chapterQuizOn, setChapterQuizOn] = useState(true);
  const [chapterQuizCount, setChapterQuizCount] = useState("10 Questions");
  const [finalExamOn, setFinalExamOn] = useState(true);
  const [finalExamCount, setFinalExamCount] = useState("50 Questions");

  // Learning Experience
  const [learningExperience, setLearningExperience] = useState({
    digitalTwin: true,
    videoIntros: true,
    workbook: true,
    realWorld: true,
    discussion: true,
    certificate: true,
  });
  const toggleLearningExperience = (
    key: keyof typeof learningExperience
  ) =>
    setLearningExperience((prev) => ({ ...prev, [key]: !prev[key] }));

  // Pricing
  const [pricingType, setPricingType] = useState<"paid" | "free">(() =>
    derivePricingType(data)
  );
  const [coursePrice, setCoursePrice] = useState(() => derivePrice(data));
  const aiRecommendedPrice = derivePrice(data);

  // Re-derive the fields above whenever a *different* course is passed in
  // (the dialog instance is often reused across course selections rather
  // than unmounted, so plain useState initializers alone won't update it).
  // useEffect(() => {
  //   if (!data) return;
  //   setChapters(deriveChapters(data));
  //   setLessonsPerChapter(deriveLessonsPerChapter(data));
  //   setDuration(deriveDuration(data));
  //   setCoursePrice(derivePrice(data));
  //   setPricingType(derivePricingType(data));
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [data?.parent_course_id, data?.course_number, data?.title]);

  useEffect(() => {
    if (!data) return;
    setWeeks(deriveWeeks(data));
    setLessonsPerChapter(deriveLessonsPerChapter(data));
    setCoursePrice(derivePrice(data));
    setPricingType(derivePricingType(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.parent_course_id, data?.course_number, data?.title]);

  // Freeze the "extra" option (if any) derived from the incoming course data.
  // This must NOT be re-derived from the live `chapters`/`lessonsPerChapter`
  // state, or selecting a different option would cause the original
  // data-derived value to disappear from the list (see bug report).
  // const initialChapters = useMemo(
  //   () => deriveChapters(data),
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [data?.parent_course_id, data?.course_number, data?.title]
  // );
  // const initialLessonsPerChapter = useMemo(
  //   () => deriveLessonsPerChapter(data),
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [data?.parent_course_id, data?.course_number, data?.title]
  // );
  // const initialDuration = useMemo(
  //   () => deriveDuration(data),
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [data?.parent_course_id, data?.course_number, data?.title]
  // );

  


  // Select option lists, extended with the real data value so it always
  // renders correctly even if it doesn't match one of the presets.
  // const chapterOptionsComputed = useMemo(
  //   () => mergeOption(chapterOptions, initialChapters),
  //   [initialChapters]
  // );
  // const lessonsPerChapterOptionsComputed = useMemo(
  //   () => mergeOption(lessonsPerChapterOptions, initialLessonsPerChapter),
  //   [initialLessonsPerChapter]
  // );
  // const durationOptionsComputed = useMemo(
  //   () => mergeOption(durationOptions, initialDuration),
  //   [initialDuration]
  // );

  const initialWeeks = useMemo(
    () => deriveWeeks(data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.parent_course_id, data?.course_number, data?.title]
  );
  const initialLessonsPerChapter = useMemo(
    () => deriveLessonsPerChapter(data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.parent_course_id, data?.course_number, data?.title]
  );

  const weekOptionsComputed = useMemo(
    () => mergeOption(weekOptions, initialWeeks),
    [initialWeeks]
  );
  const lessonsPerChapterOptionsComputed = useMemo(
    () => mergeOption(lessonsPerChapterOptions, initialLessonsPerChapter),
    [initialLessonsPerChapter]
  );

  /* -------------------------------------------------------- */
  /* Reactive numbers for the right-hand preview panels          */
  /* -------------------------------------------------------- */
  //const chaptersNum = parseInt(chapters, 10) || 0;
  const weeksNum = parseInt(weeks, 10) || 0;
  const lessonsPerChapterNum = parseInt(lessonsPerChapter, 10) || 0;
  //const totalLessons = chaptersNum * lessonsPerChapterNum;
  const totalLessons = weeksNum * lessonsPerChapterNum;
  const lessonQuizNum = parseInt(lessonQuizCount, 10) || 0;
  const chapterQuizNum = parseInt(chapterQuizCount, 10) || 0;
  const finalExamNum = parseInt(finalExamCount, 10) || 0;
  const totalQuizQuestions =
    (lessonQuizOn ? totalLessons * lessonQuizNum : 0) +
    (chapterQuizOn ? weeksNum * chapterQuizNum : 0);

  const previewItems = useMemo(
    () => [
      { icon: <ViewModuleIcon />, value: String(weeksNum), label: "Modules" },
      { icon: <MenuBookIcon />, value: String(totalLessons), label: "Lessons" },
      // { icon: <AccessTimeIcon />, value: duration, label: "Estimated Duration" },
      /*{
        icon: <HelpOutlineIcon />,
        value: `${totalQuizQuestions}+`,
        label: "Quiz Questions",
      },
      {
        icon: <CheckCircleOutlineIcon />,
        value: finalExamOn ? String(finalExamNum) : "0",
        label: "Final Exam Questions",
      },*/
      {
        icon: <WorkspacePremiumIcon />,
        value: learningExperience.certificate ? "Yes" : "No",
        label: "Certificate of Completion",
      },
      /*{
        icon: <DescriptionOutlinedIcon />,
        value: learningExperience.workbook ? "Yes" : "No",
        label: "Workbook Included",
      },*/
      { icon: <PersonOutlineIcon />, value: "My Digital Twin", label: "AI Instructor" },
    ],
    [
      weeksNum,
      totalLessons,
      totalQuizQuestions,
      finalExamOn,
      finalExamNum,
      learningExperience.certificate,
      learningExperience.workbook,
    ]
  );

  // Course Outline preview: built from the real curriculum when available,
  // otherwise falls back to the static example so the dialog still looks
  // right with no data (e.g. a "start from scratch" flow).
  const outlineChapters = useMemo(() => {
    if (curriculum.length) {
      return curriculum
        .slice()
        .sort((a, b) => a.week - b.week)
        .map((w) => ({
          week: `Module ${w.week}: ${w.title}`,
          lessons: w.subtopics && w.subtopics.length ? w.subtopics : ["Overview"],
        }));
    }
    return defaultCourseOutlinePreview;
  }, [curriculum]);

  const [showFullOutline, setShowFullOutline] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const outlineShown = showFullOutline
    ? outlineChapters
    : outlineChapters.slice(0, 2);
  const outlineMoreCount = outlineChapters.length - outlineShown.length;

  // "What Your Course Will Include" checklist, driven by current selections.
  const whatYouGetItems = useMemo(() => {
    if (!curriculum.length) return defaultWhatInclude;
    const items: string[] = [`${weeksNum} Modules`, `${totalLessons} Lessons`];
    if (learningExperience.videoIntros) items.push("Video Introductions");
    //if (lessonQuizOn) items.push(`${totalQuizQuestions}+ Lesson Quiz Questions`);
    //if (finalExamOn) items.push(`${finalExamNum} Final Exam Questions`);
    //if (learningExperience.workbook) items.push("Downloadable Workbook");
    if (learningExperience.certificate) items.push("Completion Certificate");
    items.push("Course Resources");
    return items;
  }, [
    curriculum.length,
    weeksNum,
    totalLessons,
    learningExperience.videoIntros,
    learningExperience.workbook,
    learningExperience.certificate,
    lessonQuizOn,
    totalQuizQuestions,
    finalExamOn,
    finalExamNum,
  ]);
  const midpoint = Math.ceil(whatYouGetItems.length / 2);
  const whatIncludeLeft = whatYouGetItems.slice(0, midpoint);
  const whatIncludeRight = whatYouGetItems.slice(midpoint);

  const headerSubtitle = data?.title
    ? `Set your preferences for "${data.title}" and let AI create your course automatically.`
    : "Set your preferences and let AI create your course automatically.";

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const selectedLibraryItems: KnowledgeLibraryPayloadItem[] = libraryItems
        .filter((i) => selectedLibraryIds.has(i.id))
        .map((i) => ({
          ...i,
          type: getItemCategory(i.type), // e.g. "video" / "audio" instead of "MP4"
          format: i.type, // original extension/tag, e.g. "MP4", "MP3", "PDF"
        }));

      await onGenerate?.({
        sourceCourse: data,
        libraryItems: selectedLibraryItems,
        weeks,
        lessonsPerChapter,
        lessonLength,
        courseType,
        teachingStyle,
        learnerLevel,
        generationMode,
        content_language: contentLanguage,
        lessonQuiz: { enabled: lessonQuizOn, count: lessonQuizCount },
        chapterQuiz: { enabled: chapterQuizOn, count: chapterQuizCount },
        finalExam: { enabled: finalExamOn, count: finalExamCount },
        learningExperience,
        pricingType,
        coursePrice: pricingType === "paid" ? coursePrice : "0",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0,0,0,0.75)",
          backdropFilter: "blur(4px)",
        },
      }}
      PaperProps={{
        sx: {
          background:
            "linear-gradient(180deg, rgba(17,24,39,0.98) 0%, rgba(8,12,20,0.98) 100%)",
          border: "1px solid rgba(255,255,255,0.35)",
          borderRadius: "24px",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box p={{ xs: 2.5, md: 3.5 }}>
          {/* HEADER */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Stack direction="row" spacing={1.8} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "14px",
                  background: "linear-gradient(135deg,#7C3AED,#9333EA)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 30px rgba(124,58,237,0.35)",
                }}
              >
                <SchoolIcon sx={{ color: "#fff", fontSize: 22 }} />
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: 24,
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  Customize Course
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.55)",
                    fontSize: 13.5,
                    mt: 0.4,
                  }}
                >
                  {headerSubtitle}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <IconButton onClick={onClose} size="small">
                <CloseIcon sx={{ color: "#fff" }} fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          {/* MAIN GRID */}
          <Grid container spacing={3}>
            {/* LEFT SECTION */}
            <Grid item xs={12} md={8.5}>
              <Stack spacing={3.5}>
                {/* 1. COURSE CONTENT + ADD MORE CONTENT + COURSE STRUCTURE */}
                <Grid container spacing={2.5}>
                  {/* Knowledge Library */}
                  <Grid item xs={12} md={5}>
                    <Typography sx={sectionTitleSx}>
                      1. Course Content
                    </Typography>
                    <Typography sx={sectionSubtitleSx}>
                      Choose the knowledge sources to build this course.
                    </Typography>

                    <Box sx={{ ...cardSx, p: 2 }}>
                      <Typography
                        sx={{ color: "#fff", fontWeight: 600, fontSize: 13.5 }}
                      >
                        Use My Existing Content (Knowledge Library)
                      </Typography>
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.45)",
                          fontSize: 11.5,
                          mb: 1.5,
                        }}
                      >
                        Search and select from your uploaded content.
                      </Typography>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search your content..."
                        value={librarySearch}
                        onChange={(e) => setLibrarySearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{
                                  color: "rgba(255,255,255,0.35)",
                                  fontSize: 18,
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 1.5,
                          "& .MuiOutlinedInput-root": {
                            background: "rgba(255,255,255,0.03)",
                            borderRadius: "10px",
                            color: "#fff",
                            fontSize: 13,
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#8B5CF6",
                            },
                          },
                        }}
                      />

                      {!libraryLoading && !libraryError && libraryItems.length > 0 && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            sx={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}
                          >
                            {filteredLibraryItems.length} item
                            {filteredLibraryItems.length === 1 ? "" : "s"}
                          </Typography>
                          <Typography
                            onClick={toggleSelectAllLibrary}
                            sx={{
                              color: "#A78BFA",
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            {allFilteredLibrarySelected
                              ? "Deselect All"
                              : "Select All"}
                          </Typography>
                        </Stack>
                      )}

                      {libraryLoading && (
                        <Stack
                          direction="row"
                          spacing={1.2}
                          alignItems="center"
                          justifyContent="center"
                          sx={{ py: 3 }}
                        >
                          <CircularProgress size={18} sx={{ color: "#A78BFA" }} />
                          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12.5 }}>
                            Loading your content...
                          </Typography>
                        </Stack>
                      )}

                      {!libraryLoading && libraryError && (
                        <Stack spacing={1} sx={{ py: 2.5, textAlign: "center" }}>
                          <Typography sx={{ color: "#F87171", fontSize: 12.5 }}>
                            {libraryError}
                          </Typography>
                          <Typography
                            onClick={fetchKnowledgeLibrary}
                            sx={{
                              color: "#A78BFA",
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            Retry
                          </Typography>
                        </Stack>
                      )}

                      {!libraryLoading && !libraryError && libraryItems.length === 0 && (
                        <Typography
                          sx={{
                            color: "rgba(255,255,255,0.4)",
                            fontSize: 12.5,
                            textAlign: "center",
                            py: 2.5,
                          }}
                        >
                          No uploaded content yet. Add files below to build
                          your library.
                        </Typography>
                      )}

                      {!libraryLoading && !libraryError && libraryItems.length > 0 && (
                        <Stack
                          divider={<Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }} />}
                          sx={{
                            maxHeight: 260,
                            overflowY: "auto",
                            pr: 0.5,
                            "&::-webkit-scrollbar": {
                              width: 6,
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "rgba(255,255,255,0.15)",
                              borderRadius: 3,
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          {pagedLibraryItems.map((item) => {
                            const visual = getFileVisual(item.type);
                            return (
                              <Stack
                                key={item.id}
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ py: 0.7 }}
                              >
                                <Checkbox
                                  checked={selectedLibraryIds.has(item.id)}
                                  onChange={() => toggleLibraryItem(item.id)}
                                  size="small"
                                  sx={{
                                    p: 0.4,
                                    color: "rgba(255,255,255,0.25)",
                                    "&.Mui-checked": { color: "#8B5CF6" },
                                  }}
                                />
                                <Box sx={{ color: visual.color, display: "flex" }}>
                                  {visual.icon}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    noWrap
                                    sx={{ color: "#fff", fontSize: 12.5 }}
                                  >
                                    {item.name}
                                  </Typography>
                                  <Typography
                                    noWrap
                                    sx={{
                                      color: "rgba(255,255,255,0.35)",
                                      fontSize: 10,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    ID: {item.id}
                                    {item.meta ? ` · ${item.meta}` : ""}
                                  </Typography>
                                </Box>
                                <Typography
                                  sx={{
                                    color: "rgba(255,255,255,0.4)",
                                    fontSize: 10.5,
                                    flexShrink: 0,
                                  }}
                                >
                                  {item.type}
                                </Typography>
                              </Stack>
                            );
                          })}
                          {filteredLibraryItems.length === 0 && (
                            <Typography
                              sx={{
                                color: "rgba(255,255,255,0.4)",
                                fontSize: 12,
                                textAlign: "center",
                                py: 2,
                              }}
                            >
                              No content matches "{librarySearch}".
                            </Typography>
                          )}
                        </Stack>
                      )}

                      {!libraryLoading && !libraryError && libraryPageCount > 1 && (
                        <Stack alignItems="center" sx={{ mt: 1.5 }}>
                          <Pagination
                            count={libraryPageCount}
                            page={libraryPage}
                            onChange={(_e, value) => setLibraryPage(value)}
                            size="small"
                            siblingCount={0}
                            sx={{
                              "& .MuiPaginationItem-root": {
                                color: "rgba(255,255,255,0.6)",
                              },
                              "& .Mui-selected": {
                                background: "rgba(139,92,246,0.25) !important",
                                color: "#fff",
                              },
                            }}
                          />
                        </Stack>
                      )}

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          mt: 1.5,
                          pt: 1.5,
                          borderTop: "1px solid rgba(255,255,255,0.06)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "rgba(255,255,255,0.45)",
                            fontSize: 12,
                          }}
                        >
                          {selectedLibraryCount} of {libraryItems.length}{" "}
                          selected
                        </Typography>
                        <Typography
                          onClick={fetchKnowledgeLibrary}
                          sx={{
                            color: "#A78BFA",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Refresh Library
                        </Typography>
                      </Stack>
                    </Box>
                  </Grid>

                  {/* Course Structure */}
                  <Grid item xs={12} md={7}>
                    <Stack spacing={3}>
                      <Box>
                        <Typography sx={sectionTitleSx}>
                          2. Course Structure
                        </Typography>
                        <Box sx={{ mb: 1.8 }} />

                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={4}>
                            <FieldBlock label="Modules" info>
                              <IconSelect
                                icon={<ViewModuleIcon fontSize="small" />}
                                value={weeks}
                                // onChange={setWeeks}
                                onChange={handleWeeksChange}
                                options={weekOptionsComputed}
                              />
                            </FieldBlock>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FieldBlock label="Lessons per Module" info>
                              <IconSelect
                                icon={<FormatListBulletedIcon fontSize="small" />}
                                value={lessonsPerChapter}
                                // onChange={setLessonsPerChapter}
                                onChange={handleLessonsPerChapterChange}
                                options={lessonsPerChapterOptionsComputed}
                              />
                            </FieldBlock>
                          </Grid>
                          {SHOW_LESSON_LENGTH && (
                            <Grid item xs={12} sm={4}>
                              <FieldBlock label="Lesson Length" info>
                                <IconSelect
                                  icon={<AccessTimeIcon fontSize="small" />}
                                  value={lessonLength}
                                  onChange={setLessonLength}
                                  options={lessonLengthOptions}
                                />
                              </FieldBlock>
                            </Grid>
                          )}
                        </Grid>
                      </Box>

                      {/* 3. COURSE SETTINGS */}
                      <Box>
                        <Typography sx={sectionTitleSx}>
                          3. Course Settings
                        </Typography>
                        <Box sx={{ mb: 1.8 }} />

                        <Grid container spacing={2} sx={{ mb: 2.5 }}>
                          <Grid item xs={12} sm={6}>
                            <FieldBlock label="Course Type" info>
                              <IconSelect
                                icon={<SchoolIcon fontSize="small" />}
                                value={courseType}
                                onChange={setCourseType}
                                options={courseTypeOptions.map((o) => o.label)}
                                sublabel={
                                  courseTypeOptions.find(
                                    (o) => o.label === courseType
                                  )?.sub
                                }
                              />
                            </FieldBlock>
                          </Grid>
                          {/* <Grid item xs={12} sm={6}>
                            <FieldBlock label="Estimated Duration" info>
                              <IconSelect
                                icon={<AccessTimeIcon fontSize="small" />}
                                value={duration}
                                onChange={setDuration}
                                options={durationOptionsComputed}
                              />
                            </FieldBlock>
                          </Grid> */}
                          <Grid item xs={12} sm={6}>
                            <FieldBlock label="Teaching Style" info>
                              <IconSelect
                                icon={<WorkOutlineIcon fontSize="small" />}
                                value={teachingStyle}
                                onChange={setTeachingStyle}
                                options={teachingStyleOptions}
                              />
                            </FieldBlock>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FieldBlock label="Learner Level" info>
                              <IconSelect
                                icon={<BarChartIcon fontSize="small" />}
                                value={learnerLevel}
                                onChange={setLearnerLevel}
                                options={learnerLevelOptions}
                              />
                            </FieldBlock>
                          </Grid>
                        </Grid>

                        <FieldBlock label="Generation Mode" info>
                          <Grid container spacing={2} sx={{ mt: 0.2 }}>
                            {generationModes.map((mode) => (
                              <Grid item xs={12} sm={6} key={mode.key}>
                                <RadioOption
                                  selected={generationMode === mode.key}
                                  onClick={() => setGenerationMode(mode.key)}
                                  title={mode.label}
                                  subtitle={mode.desc}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </FieldBlock>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>

                {/* 4. ASSESSMENTS + 5. LEARNING EXPERIENCE */}
                {SHOW_ASSESSMENTS_AND_LEARNING_EXPERIENCE && (
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={7}>
                    <Typography sx={sectionTitleSx}>
                      4. Assessments
                    </Typography>
                    <Box sx={{ mb: 1.8 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Stack spacing={1}>
                          <Typography
                            sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12.5 }}
                          >
                            Lesson Quizzes
                          </Typography>
                          <CheckCard
                            checked={lessonQuizOn}
                            onChange={setLessonQuizOn}
                            title="After every lesson"
                            subtitle=""
                          />
                          <IconSelect
                            icon={<HelpOutlineIcon fontSize="small" />}
                            value={lessonQuizCount}
                            onChange={setLessonQuizCount}
                            options={lessonQuizOptions}
                          />
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Stack spacing={1}>
                          <Typography
                            sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12.5 }}
                          >
                            Chapter Quizzes
                          </Typography>
                          <CheckCard
                            checked={chapterQuizOn}
                            onChange={setChapterQuizOn}
                            title="End of each chapter"
                            subtitle=""
                          />
                          <IconSelect
                            icon={<FormatListBulletedIcon fontSize="small" />}
                            value={chapterQuizCount}
                            onChange={setChapterQuizCount}
                            options={chapterQuizOptions}
                          />
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Stack spacing={1}>
                          <Typography
                            sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12.5 }}
                          >
                            Final Exam
                          </Typography>
                          <CheckCard
                            checked={finalExamOn}
                            onChange={setFinalExamOn}
                            title="At the end of the course"
                            subtitle=""
                          />
                          <IconSelect
                            icon={<AccessTimeIcon fontSize="small" />}
                            value={finalExamCount}
                            onChange={setFinalExamCount}
                            options={finalExamOptions}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.3 }}>
                      <Typography sx={sectionTitleSx}>
                        5. Learning Experience (AI Instructor & Extras)
                      </Typography>
                      <InfoDot />
                    </Stack>
                    <Box sx={{ mb: 1.2 }} />

                    <Grid container spacing={1.2}>
                      {learningExperienceItems.map((item) => (
                        <Grid item xs={4} key={item.key}>
                          <IconTile
                            icon={item.icon}
                            label={item.label}
                            checked={
                              learningExperience[
                                item.key as keyof typeof learningExperience
                              ]
                            }
                            onChange={() =>
                              toggleLearningExperience(
                                item.key as keyof typeof learningExperience
                              )
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
                )}

                {/* 6. PRICING */}
                <Box>
                  <Typography sx={sectionTitleSx}>
                    4. Pricing (Monetization)
                  </Typography>
                  <Typography sx={sectionSubtitleSx}>
                    Set a price for your course or offer it for free.
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <Stack spacing={1.6}>
                        <RadioOption
                          selected={pricingType === "paid"}
                          onClick={() => setPricingType("paid")}
                          title="Paid Course"
                          subtitle="Charge for access to this course"
                        />
                        <RadioOption
                          selected={pricingType === "free"}
                          onClick={() => setPricingType("free")}
                          title="Free Course"
                          subtitle="Offer your course for free"
                        />
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FieldBlock label="AI Recommended Price" info>
                        <Typography
                          sx={{ color: "#fff", fontWeight: 700, fontSize: 22 }}
                        >
                          ${aiRecommendedPrice}{" "}
                          <Box
                            component="span"
                            sx={{
                              color: "rgba(255,255,255,0.4)",
                              fontSize: 13,
                              fontWeight: 500,
                            }}
                          >
                            USD
                          </Box>
                        </Typography>
                        <Typography
                          sx={{
                            color: "rgba(255,255,255,0.45)",
                            fontSize: 11.5,
                            mt: 0.3,
                          }}
                        >
                          Based on course length, content depth, and market
                          analysis.
                        </Typography>
                      </FieldBlock>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FieldBlock label="Your Course Price">
                        <TextField
                          fullWidth
                          value={coursePrice}
                          onChange={(e) => setCoursePrice(e.target.value)}
                          disabled={pricingType === "free"}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>
                                  $
                                </Typography>
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
                                  USD
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              background: "rgba(255,255,255,0.03)",
                              borderRadius: "12px",
                              color: "#fff",
                              "& fieldset": {
                                borderColor: "rgba(255,255,255,0.08)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255,255,255,0.15)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#8B5CF6",
                              },
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            color: "rgba(255,255,255,0.4)",
                            fontSize: 11.5,
                            mt: 0.6,
                          }}
                        >
                          You can change this anytime.
                        </Typography>
                      </FieldBlock>
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            </Grid>

            {/* RIGHT SECTION */}
            <Grid item xs={12} md={3.5}>
              <Stack spacing={2}>
                {/* COURSE PREVIEW */}
                <Box sx={panelSx}>
                  <Typography
                    sx={{ color: "#fff", fontWeight: 700, fontSize: 16, mb: 2 }}
                  >
                    Course Preview
                  </Typography>

                  <Stack spacing={1.8}>
                    {previewItems.map((item, idx) => (
                      <Stack key={idx} direction="row" spacing={1.3} alignItems="center">
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "10px",
                            background:
                              "linear-gradient(135deg, rgba(124,58,237,0.22), rgba(147,51,234,0.12))",
                            border: "1px solid rgba(139,92,246,0.25)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            color: "#A78BFA",
                            fontSize: 17,
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Box>
                          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 13.5 }}>
                            {item.value}
                          </Typography>
                          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 11.5 }}>
                            {item.label}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                {/* COURSE OUTLINE PREVIEW */}
                <Box sx={panelSx}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
                      Course Outline{" "}
                      <Box component="span" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 400, fontSize: 12 }}>
                        (Preview)
                      </Box>
                    </Typography>
                    {outlineChapters.length > 2 && (
                      <Typography
                        onClick={() => setShowFullOutline((prev) => !prev)}
                        sx={{
                          color: "#A78BFA",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          userSelect: "none",
                          "&:hover": { color: "#C4B5FD" },
                        }}
                      >
                        {showFullOutline ? "Show less" : "View full outline"}
                      </Typography>
                    )}
                  </Stack>

                  <Stack spacing={1.8}>
                    {outlineShown.map((ch) => (
                      <Box key={ch.week}>
                        <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 12.5, mb: 0.6 }}>
                          {ch.week}
                        </Typography>
                        <Stack spacing={0.4}>
                          {ch.lessons.map((lesson, i) => (
                            <Typography
                              key={i}
                              sx={{ color: "rgba(255,255,255,0.55)", fontSize: 11.5, pl: 1.2 }}
                            >
                              • Lesson {i + 1}: {lesson}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                    {!showFullOutline && outlineMoreCount > 0 && (
                      <Typography
                        onClick={() => setShowFullOutline(true)}
                        sx={{
                          color: "rgba(255,255,255,0.35)",
                          fontSize: 11.5,
                          fontStyle: "italic",
                          cursor: "pointer",
                          "&:hover": { color: "rgba(255,255,255,0.55)" },
                        }}
                      >
                        ...and {outlineMoreCount} more module
                        {outlineMoreCount === 1 ? "" : "s"}
                      </Typography>
                    )}
                  </Stack>
                </Box>

                {/* WHAT YOUR COURSE WILL INCLUDE */}
                <Box sx={panelSx}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15, mb: 1.5 }}>
                    What Your Course Will Include
                  </Typography>

                  <Grid container spacing={0.5}>
                    <Grid item xs={6}>
                      <Stack spacing={0.8}>
                        {whatIncludeLeft.map((item) => (
                          <Stack key={item} direction="row" spacing={0.8} alignItems="center">
                            <CheckIcon sx={{ color: "#8B5CF6", fontSize: 15 }} />
                            <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 11.5 }}>
                              {item}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack spacing={0.8}>
                        {whatIncludeRight.map((item) => (
                          <Stack key={item} direction="row" spacing={0.8} alignItems="center">
                            <CheckIcon sx={{ color: "#8B5CF6", fontSize: 15 }} />
                            <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 11.5 }}>
                              {item}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>

                {/* KNOWLEDGE COVERAGE */}
                {/* <Box sx={{ ...panelSx, textAlign: "center" }}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15, mb: 1.5, textAlign: "left" }}>
                    Knowledge Coverage
                  </Typography>

                  <Box sx={{ position: "relative", display: "inline-flex", mb: 1.5 }}>
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={84}
                      thickness={4}
                      sx={{ color: "rgba(255,255,255,0.08)", position: "absolute" }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={knowledgeCoverage}
                      size={84}
                      thickness={4}
                      sx={{ color: "#22C55E" }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ color: "#4ADE80", fontWeight: 700, fontSize: 19 }}>
                        {knowledgeCoverage}%
                      </Typography>
                    </Box>
                  </Box>

                  <Typography sx={{ color: "#4ADE80", fontWeight: 700, fontSize: 14 }}>
                    Excellent Coverage!
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12, mt: 0.5, lineHeight: 1.6 }}>
                    Your selected content provides comprehensive coverage for
                    this course.
                  </Typography>
                </Box> */}
              </Stack>
            </Grid>
          </Grid>

          {/* FOOTER */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={4}
          >
            <Button
              onClick={onBack ?? onClose}
              startIcon={<ArrowBackIcon />}
              sx={{
                color: "rgba(255,255,255,0.7)",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Back
            </Button>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, fontWeight: 600 }}>
                Content language
              </Typography>
              <ContentLanguageSelect mode="local" value={contentLanguage} onChange={setContentLanguage} />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: 18 }} />
              <Box>
                <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
                  Estimated Generation Time
                </Typography>
                <Typography sx={{ color: "#A78BFA", fontWeight: 700, fontSize: 13 }}>
                  18 – 22 minutes
                </Typography>
              </Box>
            </Stack>

            <Button
              onClick={handleGenerate}
              disabled={isGenerating}
              endIcon={
                isGenerating ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <ArrowForwardIcon />
                )
              }
              sx={{
                background: "linear-gradient(135deg,#7C3AED,#9333EA)",
                color: "#fff",
                textTransform: "none",
                borderRadius: "14px",
                px: 3,
                py: 1.2,
                fontWeight: 600,
                boxShadow: "0 10px 30px rgba(124,58,237,0.35)",
                opacity: isGenerating ? 0.75 : 1,

                "&:hover": {
                  opacity: isGenerating ? 0.75 : 0.92,
                  background: "linear-gradient(135deg,#7C3AED,#9333EA)",
                },
                "&.Mui-disabled": {
                  color: "#fff",
                },
              }}
            >
              {isGenerating ? "Generating..." : "Next: Generate Course"}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>

    {/* UPGRADE PROMPT DIALOG */}
    <Dialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(3px)",
          },
        }}
        PaperProps={{
          sx: {
            background:
              "linear-gradient(180deg, rgba(24,20,42,0.98) 0%, rgba(10,8,18,0.98) 100%)",
            border: "1px solid rgba(139,92,246,0.3)",
            borderRadius: "20px",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ p: 3.5, textAlign: "center" }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              mx: "auto",
              mb: 2.5,
              background: "linear-gradient(135deg,#7C3AED,#9333EA)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 10px 30px rgba(124,58,237,0.4)",
            }}
          >
            <LockOutlinedIcon sx={{ color: "#fff", fontSize: 26 }} />
          </Box>

          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 19, mb: 1 }}>
            Unlock Full Customization
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.6)",
              fontSize: 13.5,
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            Your current plan comes with a fixed course structure of 4
            Modules and 2 Lessons per Module. Upgrade your plan to unlock
            more Modules, more Lessons, and full control over how your
            course is built.
          </Typography>

          <Button
            fullWidth
            onClick={() => setUpgradeDialogOpen(false)}
            sx={{
              background: "linear-gradient(135deg,#7C3AED,#9333EA)",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "12px",
              py: 1.1,
              boxShadow: "0 8px 24px rgba(124,58,237,0.35)",
              "&:hover": {
                opacity: 0.92,
                background: "linear-gradient(135deg,#7C3AED,#9333EA)",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
 
     </>

  );

  
};

export default CustomizeCourseDialog;
